/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';

import { useSessionData } from '@confluence/session-data';
import { useFeatureDiscovery } from '@confluence/feature-discovery';

import type {
	useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery as useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery$data,
	useSpotlightEligibilityCheckFeatureSpotlightUnifiedQueryVariables as useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery$variables,
} from './__types__/useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery';

const useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery = gql`
	query useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery($contentId: ID!) {
		getSmartContentFeature(contentId: $contentId) {
			readTime
		}
		contentContributors(id: $contentId) {
			isCurrentUserContributor
		}
	}
`;

type SpotlightEligibilityCheckData = {
	isEligibleForSpotlight: boolean;
	stopFeatureSpotlight: () => void;
	spotlightEligibilityError?: Error;
	isCurrentUserContributor?: boolean | null;
};

export const useSpotlightEligibilityCheck = (
	contentId: string,
	source: string,
): SpotlightEligibilityCheckData => {
	const { isAdminHubAIEnabled } = useSessionData();
	const [showFeatureSpotlight, stopFeatureSpotlight, featureSpotlightError] = useFeatureDiscovery(
		'quick.summary.ga.feature.spotlight.one',
	);

	const { data, error: unifiedQueryError } = useQuery<
		useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery$data,
		useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery$variables
	>(useSpotlightEligibilityCheckFeatureSpotlightUnifiedQuery, {
		variables: { contentId },
		errorPolicy: 'none',
		fetchPolicy: 'network-only',
	});
	const readTime = data?.getSmartContentFeature?.readTime ?? 0;
	const isCurrentUserContributor = data?.contentContributors?.isCurrentUserContributor;

	const isEligibleForSpotlight =
		isAdminHubAIEnabled &&
		showFeatureSpotlight &&
		!featureSpotlightError &&
		source === 'page' &&
		readTime >= 4 &&
		!isCurrentUserContributor;

	const spotlightEligibilityError = featureSpotlightError || unifiedQueryError;

	return {
		isEligibleForSpotlight,
		stopFeatureSpotlight,
		spotlightEligibilityError,
		isCurrentUserContributor,
	};
};
